import { useTranslation } from 'react-i18next';
import { DISTRICT_DEFS } from '@pk/shared';
import type { DistrictKey, DistrictOptionView, HouseView, Id, TitleView, WorldView } from '@pk/shared';
import { useCommand } from '../api/hooks';
import { Bar, Btn, Collapsible, Inline, Panel, Tag } from '../components/ui';
import { IconCrown, IconRealm, ResGold } from '../components/icons';
import { useHourEta } from '../components/game';

type Cmd = ReturnType<typeof useCommand>;
interface Props { title: TitleView; house: HouseView; world: WorldView; mine: boolean; actor: Id }

const sign = (n: number): string => (n > 0 ? `+${n}` : `${n}`);
const r1 = (n: number): number => Math.round(n * 10) / 10;

/** Population, prosperity, development project, slots and income of a holding (Design pass 1). */
export function HoldingEconomy({ title, house, mine, actor }: Props) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const hourEta = useHourEta();
  const e = title.economy;
  if (!e) return null;
  const next = e.nextDevelopment;
  const res = house.house.resources;
  const missing = next ? (['timber', 'stone', 'iron'] as const).filter((k) => res[k] < next[k]) : [];
  const reason = next ? (next.blocked ?? (res.gold < next.gold ? t('econ.dev.noGold', { n: next.gold }) : null)) : null;
  const upkeepTone = e.prosperityChange < 0 ? 'red' : 'green';

  return (
    <>
      <div className="attrs">
        <Bar label={t('title.development')} value={e.development} max={10} />
        <Bar label={t('econ.prosperity')} value={e.prosperity} max={100} tone={e.prosperity < 20 ? 'red' : 'green'} />
        <div className="kv"><span>{t('econ.population')}</span><span className="num">{e.population} / {e.popCap} <span className="muted small">({sign(e.popChange)} {t('econ.perMonth')})</span></span></div>
        <div className="kv"><span>{t('econ.slots')}</span><span className="num">{e.slots.used} / {e.slots.total}</span></div>
        {mine && <div className="kv"><span>{t('econ.food')}</span><span className="num">{e.foodNeed} {t('econ.perMonth')}</span></div>}
        <div className="kv"><span>{t('econ.income')}</span><span className="num"><ResGold size={16} /> {r1(e.income.total)} <span className="muted small">{t('econ.perMonth')}</span></span></div>
      </div>

      <Collapsible title={t('econ.prosperityChange')} summary={<Tag tone={upkeepTone}>{sign(e.prosperityChange)}</Tag>} className="flush">
        {e.prosperityTerms.length === 0 && <p className="small muted">{t('common.none')}</p>}
        {e.prosperityTerms.map((x) => (
          <div className="kv" key={x.key}><span>{t(`econ.term.${x.key}`)}</span><span className="num">{sign(x.value)}</span></div>
        ))}
        {e.lowProsperityMonths > 0 && <p className="small muted">{t('econ.decayWarning', { n: e.lowProsperityMonths })}</p>}
      </Collapsible>

      <Collapsible title={t('econ.incomeBreakdown')} className="flush">
        <div className="kv"><span>{t('econ.inc.base')}</span><span className="num">{e.income.base}</span></div>
        {e.income.marketBonus > 0 && <div className="kv"><span>{t('econ.inc.market')}</span><span className="num">+{Math.round(e.income.marketBonus * 100)}%</span></div>}
        {e.income.stewardBonus > 0 && <div className="kv"><span>{t('econ.inc.steward')}</span><span className="num">+{Math.round(e.income.stewardBonus * 100)}%</span></div>}
        {e.income.demesneMult < 1 && <div className="kv"><span>{t('econ.inc.demesne')}</span><span className="num">-{Math.round((1 - e.income.demesneMult) * 100)}%</span></div>}
        {e.income.distanceMult < 1 && <div className="kv"><span>{t('econ.inc.distance')}</span><span className="num">-{Math.round((1 - e.income.distanceMult) * 100)}%</span></div>}
        <div className="kv"><span><b>{t('econ.inc.total')}</b></span><span className="num"><b>{r1(e.income.total)}</b></span></div>
      </Collapsible>

      <h3 className="subhead">{t('econ.dev.title')}</h3>
      {e.devProject ? (
        <p className="small">{t('econ.dev.running', { target: e.devProject.target, when: hourEta(e.devProject.doneHour).when, eta: hourEta(e.devProject.doneHour).eta })}</p>
      ) : next ? (
        <div className="bld">
          <p className="small">{t('econ.dev.cost', {
            target: next.target, gold: next.gold, timber: next.timber, stone: next.stone, iron: next.iron, months: next.months, pop: next.popNeeded,
          })}</p>
          {missing.length > 0 && mine && <p className="small muted">{t('econ.dev.missing', { list: missing.map((k) => t(`resource.${k}`).toLowerCase()).join(', ') })}</p>}
          {mine && (
            <>
              <Btn small variant="ghost" disabled={!!reason} busy={cmd.pending} title={reason ?? undefined}
                onClick={() => void cmd.run({ type: 'start_development', actorId: actor, titleId: title.id })}>
                {t('econ.dev.start', { target: next.target })}
              </Btn>
              {reason && <span className="small muted" style={{ marginLeft: 8 }}>{reason}</span>}
            </>
          )}
        </div>
      ) : <p className="small muted">{t('econ.dev.max')}</p>}
      <Inline error={cmd.error} />
    </>
  );
}

/** City tier, treasury, commerce, districts, licences, and the Lord Mayor's treasury actions. */
export function CityEconomy({ title, mine, actor }: Props) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const hourEta = useHourEta();
  const c = title.city;
  if (!c) return null;
  const dev = title.economy;
  return (
    <Panel icon={<IconCrown />} title={t('econ.city.title')} right={<Tag tone="gold">{t(`econ.tier.${c.tier}`)}</Tag>}>
      <div className="attrs">
        <div className="kv"><span>{t('econ.city.treasury')}</span><span className="num"><ResGold size={16} /> {Math.floor(c.treasury)}</span></div>
        <div className="kv"><span>{t('econ.city.commerce')}</span><span className="num">{c.commerce} <span className="muted small">(+{c.commerceGain})</span></span></div>
        <div className="kv"><span>{t('econ.city.licences')}</span><span className="num">{c.licencesUsed} / {c.licencesTotal}</span></div>
        <div className="kv"><span>{t('econ.city.tariffCap')}</span><span className="num">{Math.round(c.tariffCap * 100)}%</span></div>
        {c.guildVoteWeight > 1 && <div className="kv"><span>{t('econ.city.guildVote')}</span><span className="num">x{c.guildVoteWeight}</span></div>}
        {c.greatCity && <p className="small muted">{t('econ.city.greatCity')}</p>}
      </div>

      {mine && dev && !dev.devProject && dev.nextDevelopment && (
        <>
          <Btn small variant="ghost" disabled={!!c.fundBlocked} busy={cmd.pending} title={c.fundBlocked ?? undefined}
            onClick={() => void cmd.run({ type: 'fund_city_development', actorId: actor, cityId: title.id })}>
            {t('econ.city.fund', { target: dev.nextDevelopment.target, gold: dev.nextDevelopment.gold })}
          </Btn>
          {c.fundBlocked && <span className="small muted" style={{ marginLeft: 8 }}>{c.fundBlocked}</span>}
        </>
      )}

      <h3 className="subhead">{t('econ.city.districts', { n: c.districts.length, max: c.districtSlots })}</h3>
      {c.districts.length === 0 && !c.districtProject && <p className="small muted">{c.districtSlots === 0 ? t('econ.city.noSlots') : t('common.none')}</p>}
      {c.districts.map((k) => (
        <div key={k} className="bld"><div className="row between"><h3>{DISTRICT_DEFS[k].name}</h3></div><p className="small muted" style={{ margin: '2px 0' }}>{DISTRICT_DEFS[k].description}</p></div>
      ))}
      {c.districtProject && (
        <div className="bld">
          <div className="row between"><h3>{DISTRICT_DEFS[c.districtProject.district].name}</h3><Tag tone="blue">{t('title.building')}</Tag></div>
          <p className="small">{t('econ.city.districtRunning', { when: hourEta(c.districtProject.doneHour).when, eta: hourEta(c.districtProject.doneHour).eta })}</p>
        </div>
      )}
      {mine && c.districtSlots > c.districts.length && !c.districtProject && (
        <Collapsible title={t('econ.city.build')} summary={c.districtOptions.length} className="flush">
          {c.districtOptions.map((o) => (
            <DistrictOption key={o.key} o={o} cmd={cmd} cityId={title.id} actor={actor} />
          ))}
        </Collapsible>
      )}
      <Inline error={cmd.error} />
    </Panel>
  );
}

function DistrictOption({ o, cmd, cityId, actor }: { o: DistrictOptionView; cmd: Cmd; cityId: Id; actor: Id }) {
  const { t } = useTranslation();
  const key = o.key as DistrictKey;
  return (
    <div className="bld">
      <div className="row between"><h3>{o.name}</h3></div>
      <p className="small muted" style={{ margin: '2px 0 6px' }}>{o.description}</p>
      <p className="small">{t('econ.city.districtCost', { gold: o.cost, months: o.months })}</p>
      <Btn small variant="ghost" disabled={!!o.blocked} busy={cmd.pending} title={o.blocked ?? undefined}
        onClick={() => void cmd.run({ type: 'build_district', actorId: actor, cityId, district: key })}>
        {t('econ.city.buildDistrict')}
      </Btn>
      {o.blocked && <span className="small muted" style={{ marginLeft: 8 }}>{o.blocked}</span>}
    </div>
  );
}

/** Demesne limit, vassal count and the penalties that come with too much land. */
export function RealmSizePanel({ title }: { title: TitleView }) {
  const { t } = useTranslation();
  const r = title.realmSize;
  if (!r) return null;
  const over = r.excess > 0 || r.vassals > r.vassalLimit || r.farHoldings > 0;
  return (
    <Panel icon={<IconRealm />} title={t('econ.realm.title')} right={over ? <Tag tone="red">{t('econ.realm.over')}</Tag> : undefined}>
      <div className="kv"><span>{t('econ.realm.demesne')}</span><span className="num">{r.holdings} / {r.limit}</span></div>
      {r.excess > 0 && <p className="small">{t('econ.realm.excess', { n: r.excess, income: Math.round(r.incomePenalty * 100), opinion: r.excess * 5 })}</p>}
      <div className="kv"><span>{t('econ.realm.vassals')}</span><span className="num">{r.vassals} / {r.vassalLimit}</span></div>
      {r.vassalOpinionPenalty > 0 && <p className="small">{t('econ.realm.vassalExcess', { opinion: r.vassalOpinionPenalty })}</p>}
      {r.farHoldings > 0 && <p className="small">{t('econ.realm.far', { n: r.farHoldings })}</p>}
      {!over && <p className="small muted">{t('econ.realm.fine')}</p>}
    </Panel>
  );
}
